import React, { useMemo, useState, useContext } from 'react';
import { Box, Typography, LinearProgress, Tooltip, Stack, Chip, Paper } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UserContext from '../../User/UserSettings/UserContext';

export interface StorageHealthData {
    storageId: string;
    storageName: string;
    healthIndex: number | null;
}

interface HealthIndexMetricProps {
    storageData: StorageHealthData[];
    onStorageSelect?: (storageId: string) => void;
}

const getHealthIndexColor = (value: number | null | undefined, theme: any) => {
    if (value === null || value === undefined) {
        return theme.palette.grey[400];
    }
    if (value >= 80) return theme.palette.success.main;
    if (value >= 60) return theme.palette.warning.main;
    if (value >= 40) return theme.palette.warning.dark;
    return theme.palette.error.main;
};

const HealthIndexMetric: React.FC<HealthIndexMetricProps> = ({ storageData, onStorageSelect }) => {
    const theme = useTheme();
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const { user } = useContext(UserContext) || { user: {} };

    // Calculate overall health index (average of all storages)
    const overallHealthIndex = useMemo(() => {
        const validHealthIndexes = storageData
            .map(s => s.healthIndex)
            .filter((h): h is number => h !== null && h !== undefined && !isNaN(h));
        
        if (validHealthIndexes.length === 0) return null;
        
        const sum = validHealthIndexes.reduce((acc, val) => acc + val, 0);
        return sum / validHealthIndexes.length;
    }, [storageData]);

    // Sort storages for tooltip
    const sortedByHealth = useMemo(() => {
        return [...storageData]
            .filter(s => s.healthIndex !== null && s.healthIndex !== undefined)
            .sort((a, b) => (b.healthIndex! - a.healthIndex!));
    }, [storageData]);

    // Tooltip Content
    const tooltipContent = (
        <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Health Index Breakdown
            </Typography>
            <Stack spacing={0.5} divider={<Box sx={{ height: '1px', bgcolor: 'divider', my: 0.5 }} />}>
                {sortedByHealth.map((storage) => (
                    <Box 
                        key={storage.storageId} 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onStorageSelect) {
                                onStorageSelect(storage.storageId);
                                setTooltipOpen(false);
                            }
                        }}
                        sx={{
                            cursor: onStorageSelect ? 'pointer' : 'default',
                            borderRadius: 1,
                            px: 0.5,
                            py: 0.25,
                            transition: 'background-color 0.2s',
                            '&:hover': onStorageSelect ? {
                                bgcolor: alpha(theme.palette.primary.main, 0.08)
                            } : {}
                        }}
                    >
                        <Typography variant="body2" sx={{ mr: 2 }}>
                            {storage.storageName}
                        </Typography>
                        <Chip 
                            label={`${storage.healthIndex?.toFixed(0)}%`}
                            size="small"
                            sx={{ 
                                bgcolor: alpha(getHealthIndexColor(storage.healthIndex, theme), 0.2),
                                color: getHealthIndexColor(storage.healthIndex, theme),
                                fontWeight: 600,
                                minWidth: 50
                            }}
                        />
                    </Box>
                ))}
                {sortedByHealth.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No health data available
                    </Typography>
                )}
            </Stack>
        </Box>
    );

    const healthColor = getHealthIndexColor(overallHealthIndex, theme);

    return (
        <Tooltip 
            title={tooltipContent}
            arrow
            placement="bottom"
            open={tooltipOpen}
            onClose={() => setTooltipOpen(false)}
            onOpen={() => !user?.mobile && setTooltipOpen(true)}
            disableFocusListener
            disableTouchListener
            disableHoverListener={user?.mobile}
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white',
                        color: 'text.primary',
                        border: 1,
                        borderColor: 'divider',
                        boxShadow: theme.shadows[8],
                        '& .MuiTooltip-arrow': {
                            color: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.common.white,
                            '&::before': {
                                border: 1,
                                borderColor: 'divider',
                            }
                        }
                    }
                }
            }}
        >
            <Paper 
                elevation={0}
                onClick={() => user?.mobile && setTooltipOpen((prev) => !prev)}
                sx={{ 
                    p: 1.5,
                    flex: 1,
                    border: 1, 
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: healthColor,
                        boxShadow: `0 0 0 1px ${alpha(healthColor, 0.15)}`,
                        transform: 'translateY(-1px)',
                    }
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <TrendingUpIcon sx={{ color: healthColor, fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                            Health Index
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.25}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: healthColor }}>
                            {overallHealthIndex !== null ? overallHealthIndex.toFixed(0) : '—'}
                        </Typography>
                        {overallHealthIndex !== null && (
                            <Typography variant="body2" color="text.secondary">
                                %
                            </Typography>
                        )}
                    </Box>
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={overallHealthIndex ?? 0} 
                    sx={{
                        height: 6,
                        borderRadius: 1,
                        bgcolor: alpha(healthColor, 0.15),
                        '& .MuiLinearProgress-bar': {
                            bgcolor: healthColor,
                            borderRadius: 1,
                        }
                    }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: '0.7rem' }}>
                    {sortedByHealth.length} storage{sortedByHealth.length !== 1 ? 's' : ''}
                </Typography>
            </Paper>
        </Tooltip>
    );
};

export default HealthIndexMetric;
